/**
 * Product Detail Components
 * Reusable components for product group detail pages
 * 
 * Note: Most display logic has been consolidated into ProductShowcase.js
 * This file contains only page-specific components not handled by ProductShowcase
 */
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FormattedText } from '@/lib/formatters';
import BlurImage from '@/components/BlurImage';
import CustomScrollbar from '@/components/common/CustomScrollbar';

/**
 * Breadcrumb navigation
 */
export const Breadcrumb = ({ items }) => (
  <div className="px-10 md:px-20 lg:px-28 xl:px-36 py-4 border-b border-black/5">
    <div className="max-w-[1400px] mx-auto">
      <nav className="flex items-center gap-2 text-sm">
        {items.map((item, idx) => (
          <span key={idx} className="flex items-center gap-2">
            {idx > 0 && <span className="text-site-text-muted">/</span>}
            {item.to ? (
              <Link to={item.to} className="text-site-text-muted hover:text-site-text">{item.label}</Link>
            ) : (
              <span className="text-site-text">{item.label}</span>
            )}
          </span>
        ))}
      </nav>
    </div>
  </div>
);

/**
 * Truncated text with fade and scrollbar
 * Used by HorizontalMediaSection
 */
export const TruncatedText = ({ text, isOverflowing, textRef }) => {
  if (!text) return null;
  
  return (
    <div className="min-h-0 relative mb-4 flex">
      <div ref={textRef} className="min-h-0 flex-1 overflow-hidden scrollbar-hide pr-1">
        <p className="text-sm text-site-text-muted leading-relaxed">
          <FormattedText>{text}</FormattedText>
        </p>
        {isOverflowing && (
          <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-site-bg to-transparent pointer-events-none" />
        )}
      </div>
      <CustomScrollbar scrollRef={textRef} direction="vertical" show={isOverflowing} />
    </div>
  );
};

/**
 * Horizontal media section - for additional landscape images/videos below main content
 * 
 * Layout behavior:
 * - Desktop (lg+): Text side-by-side with media, text constrained to media height
 * - Mobile (<lg): Text above media, text constrained to 5 lines (~130px)
 */
export const HorizontalMediaSection = ({ 
  media, 
  text, 
  selectedIndex, 
  onSelect, 
  isOverflowing, 
  textRef, 
  mediaRef, 
  getMediaTagLabel, 
  isVideo 
}) => {
  if (!media?.length) return null;
  
  return (
    <div className="mt-8 pt-8 border-t border-site-border">
      <div className="grid grid-cols-1 lg:grid-cols-[384px_1fr] gap-6 lg:gap-12 items-start lg:items-stretch">
        {/* Text - on mobile: order-1 (first), on desktop: order-1 (left side) */}
        {text && (
          <div className="w-full order-1 flex flex-col">
            <TruncatedText text={text} isOverflowing={isOverflowing} textRef={textRef} />
          </div>
        )}
        
        {/* Media - on mobile: order-2 (second), on desktop: order-2 (right side) */}
        <div className="space-y-3 order-2">
          <div ref={mediaRef} className="relative">
            <div className="rounded-lg overflow-hidden">
              {isVideo(media[selectedIndex]?.url) ? (
                <video 
                  src={media[selectedIndex]?.url} 
                  controls 
                  className="w-full h-auto"
                  data-testid={`horizontal-video-${selectedIndex}`}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <BlurImage 
                  src={media[selectedIndex]?.url} 
                  alt={`Image ${selectedIndex + 1}`} 
                  className="w-full h-auto object-cover" 
                  containerClassName="w-full"
                  data-testid={`horizontal-image-${selectedIndex}`} 
                />
              )}
              
              {getMediaTagLabel?.(media[selectedIndex]?.type) && (
                <div className="absolute top-3 right-3 px-2 py-1 bg-white/70 backdrop-blur-sm text-black text-[10px] font-mono uppercase tracking-wider rounded">
                  {getMediaTagLabel(media[selectedIndex]?.type)}
                </div>
              )}
            </div>
            
            {/* Navigation */}
            {media.length > 1 && (
              <>
                <button onClick={() => onSelect(selectedIndex === 0 ? media.length - 1 : selectedIndex - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-testid="prev-horizontal-btn">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => onSelect(selectedIndex === media.length - 1 ? 0 : selectedIndex + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-testid="next-horizontal-btn">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          
          {/* Dots */}
          {media.length > 1 && (
            <div className="flex gap-2 justify-center pt-4">
              {media.map((_, idx) => (
                <button key={idx} onClick={() => onSelect(idx)} className={`w-2 h-2 rounded-full transition-all ${selectedIndex === idx ? 'bg-site-bg-dark' : 'bg-zinc-300 hover:bg-zinc-400'}`} data-testid={`horizontal-dot-${idx}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default {
  Breadcrumb,
  TruncatedText,
  HorizontalMediaSection
};
